import { OrbitControls } from '@react-three/drei'
import { Canvas } from '@react-three/fiber'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { translations } from '../../../configs/translations'
import { CaseTypes } from '../../../models/CaseType'
import { Prize } from '../../../models/Prize'
import { CaseInfo } from '../../../models/response/CaseInfo'
import { UserProfile } from '../../../models/UserProfile'
import UserService from '../../../services/UserService'
import { useStore } from '../../../store/store'
import { FallingCase } from './case/FallingCase'
import { OpenCaseRoulette } from './case/OpenCaseRoulette/OpenCaseRoulette'
import './OpenCase.css'

interface OpenCaseProps {
  caseType: CaseTypes
  setProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>
}

export const OpenCase = ({ caseType, setProfile }: OpenCaseProps) => {
  const [playAnim, setPlayAnim] = useState(false)
  const [playLid, setPlayLid] = useState(false)

  const [isActionVisible, setIsActionVisible] = useState(false)

  const language = useStore(state => state.language) as keyof typeof translations
  const t = translations[language]
  const [caseInfo, setCaseInfo] = useState({} as CaseInfo)
  const [prize, setPrize] = useState<Prize | null>(null)

  const fetchCaseInfo = async () => {
    await UserService.getCaseInfo(caseType)
      .then((response) => {
        setCaseInfo(response)
      })
      .catch((error) => {
        toast.error(
          error && error.response && error.response.data && t[error.response.data as keyof typeof t]
            ? t[error.response.data as keyof typeof t]
            : t.error_unknown,
          { className: 'notification-error' }
        )
      })
  }

  const audio = new Audio('./sounds/open_case.mp3')
  const openCase = async () => {
    await UserService.openCase(caseType)
      .then((response) => {
        setPrize(response)
        audio.play()
        setPlayLid(true)
        setTimeout(() => {
          setIsActionVisible(true)
          setPlayAnim(true)
        }, 2000)
      })
      .catch((error) => {
        toast.error(
          error && error.response && error.response.data && t[error.response.data as keyof typeof t]
            ? t[error.response.data as keyof typeof t]
            : t.error_unknown,
          { className: 'notification-error' }
        )
      })
  }

  useEffect(() => {
    if (caseType != 'NONE') {
      fetchCaseInfo()
    }
  }, [caseType])

  const handleEsc = () => {
    closeAction()
  }

  const closeAction = () => {
    fetchCaseInfo()
    setIsActionVisible(false)
    setPlayAnim(false)
    setPlayLid(false)
    setIsModuleWindowOpen(false)
  }

  const buySound = new Audio('./sounds/buy.mp3')
  const getOnCaseBuyFunc = (type: string): () => void => {
    return async () => {
      await UserService.buyCase(type)
        .then(() => {
          buySound.play()
          fetchCaseInfo()
          setProfile(null)
        })
        .catch((error) => {
          toast.error(
            error && error.response && error.response.data && t[error.response.data as keyof typeof t]
              ? t[error.response.data as keyof typeof t]
              : t.error_unknown,
            { className: 'notification-error' }
          )
        })
    }
  }

  const handleTakePrize = () => {
    UserService.applyPrize(prize?.caseId || '')
      .then(() => {
        closeAction()
      })
      .catch((error) => {
        toast.error(
          error && error.response && error.response.data && t[error.response.data as keyof typeof t]
            ? t[error.response.data as keyof typeof t]
            : t.error_unknown,
          { className: 'notification-error' }
        )
      })
  }

  const [isModuleWindowOpen, setIsModuleWindowOpen] = useState(false)

  const openAlert = () => {
    setIsModuleWindowOpen(true)
  }

  const scrapSound = new Audio('./sounds/scrap.mp3')
  const handleDiscardPrize = () => {
    closeAction()
    scrapSound.play()
    UserService.discardPrize(prize?.caseId || '')
      .catch((error) => {
        toast.error(
          error && error.response && error.response.data && t[error.response.data as keyof typeof t]
            ? t[error.response.data as keyof typeof t]
            : t.error_unknown,
          { className: 'notification-error' }
        )
      })
    setIsModuleWindowOpen(false)
  }

  return (
    <>
      {isActionVisible && <OpenCaseRoulette
        caseInfo={caseInfo}
        prize={prize!}
        playAnim={playAnim}
        setPlayAnim={setPlayAnim}
        handleEsc={handleEsc}
        handleTakePrize={handleTakePrize}
        handleDiscardPrize={handleDiscardPrize}
        isModuleWindowOpen={isModuleWindowOpen}
        setIsModuleWindowOpen={setIsModuleWindowOpen}
        openAlert={openAlert}
      />}
      {caseInfo && (
        <>
          <div className='open-case' style={{ width: '100%', height: '100%' }}>
            <h1 className='open-case-title'>{t[`case_${caseType}`] || caseType}</h1>
            <Canvas className='open-case-canvas' camera={{ position: [3, 2, 5], fov: 50 }}>
              <directionalLight position={[1, 1, 1]} intensity={0.8} />
              <ambientLight intensity={1} />
              <FallingCase playLid={playLid} textureUrl={`./cases/${caseType}.png`} />
              <OrbitControls minAzimuthAngle={Math.PI / 5}
                maxAzimuthAngle={(Math.PI / 5) + 0.1}
                minPolarAngle={Math.PI / 2.6}
                maxPolarAngle={(Math.PI / 2.6) + 0.05}
                enableZoom={false}
                enablePan={false}
              />
            </Canvas>
          </div>
          <div className='open-case-content-container'>
            <div className='open-case-open-section'>
              <div className='open-case-amount'>
                <p style={{ fontSize: '20px' }}>{t.you_have}</p>
                <p style={{ fontSize: '20px', fontWeight: 'bold' }}>{caseInfo.amount || 0}</p>
              </div>
              {
                caseInfo.amount > 0 ? (
                  <div className="donate-shop-card-button-container">
                    <div
                      onClick={() => openCase()}
                      className="donate-shop-card-button"
                    >
                      <p>{t.open}</p>
                    </div>
                  </div>
                ) : (
                  <div className="donate-shop-card-button-container">
                    <div
                      onClick={() => getOnCaseBuyFunc(caseType)()}
                      className="donate-shop-card-button"
                    >
                      <p>{t.buy}</p>
                    </div>
                  </div>
                )
              }
            </div>

            <div className='open-case-prizes-section-wrapper'>
              <div className='open-case-prizes-section'>
                {caseInfo.prizes?.map((prize, index) => (
                  <div className='open-case-prize' key={index}>
                    <img src={`./cases/prizes/${prize.name}.png`} alt={prize.name} />
                    <p>{prize.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
